<template>
    <div class="space-y-6 pb-8">
        <h1 class="-mb-4 pl-2 text-2xl font-semibold uppercase tracking-wide">
            {{ planTitle }}
        </h1>

        <plan-presentation
            v-if="!isEditing"
            :plan="planData"
            :allow-bookings="allowBookings"
        />

        <template v-if="allowBookings">
            <duplicate-contract-message
                :plan-id="planData.id"
                :contract-id="contractData?.id"
                :warning-text="t('purchaseContract.duplicatePassWarning')"
                :edit-text="t('purchaseContract.editPass')"
            />

            <select-participants
                :is-active-section="activeSection === 0"
                :contract="contractData"
                :plan="planData"
                :is-mutating="isCreatingContract || isChangingContract"
                @create-contract="handleCreateContract"
                @change-contract="handleChangeContract"
                @edit-section="activeSection = 0"
            />

            <template v-if="contractData !== undefined">
                <participant-details
                    v-if="activeSection > 0"
                    :is-active-section="activeSection === 1"
                    :contract="contractData"
                    :plan="planData"
                    :is-editing="isEditing"
                    :is-updating-contract="
                        isUpdatingContract || isActivatingContract
                    "
                    @continue="handleContinue"
                    @add-to-cart="handleAddToCartParticipantDetails"
                    @activate-contract-now="handleActivateContractNow"
                    @edit-section="activeSection = 1"
                />

                <checkout-section
                    v-if="isKiosk && activeSection > 1"
                    :contract="contractData"
                />

                <document-section
                    v-if="!isKiosk && activeSection > 1"
                    :contract="contractData"
                    :is-signing-document="isSigningDocument"
                    :invalid-initials="invalidInitials"
                    :invalid-signatures="invalidSignatures"
                    @add-to-cart="addToCart"
                    @sign-document="handleSignDocument"
                />
            </template>
        </template>
    </div>
</template>

<script lang="ts">
import { graphql } from '~/resources/graphql'

export const PurchasePassPlanFragment = graphql(/** GraphQL */ `
    fragment PurchasePassPlanFragment on Plan {
        id
        planType
        name
        ...PlanPresentationPlanFragment
        ...SelectParticipantsPlanFragment
        ...ParticipantDetailsPlanFragment
        presentation(input: $input, language: $language) {
            title
        }
        checkAllowBookings: bookingLimit(input: $input) {
            allowBookings
        }
    }
`)

export const PurchasePassContractFragment = graphql(/** GraphQL */ `
    fragment PurchasePassContractFragment on PlanContract {
        ...SelectParticipantsContractFragment
        ...ParticipantDetailsContractFragment
        ...DocumentSectionContractFragment
        ...PaymentMethodInputPlanContractFragment
        id
        draftExpiresAtUtc
        updatedAtUtc
        readyForPurchase(input: { checkDocument: true })
    }
`)
</script>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import ParticipantDetails from '~/components/PurchaseContract/ParticipantDetails/ParticipantDetails.vue'
import SelectParticipants from '~/components/PurchaseContract/Membership/SelectParticipants.vue'
import DocumentSection from '~/components/PurchaseContract/DocumentSection/DocumentSection.vue'
import type {
    ChangeContractInput,
    CreateContractInput,
    PurchasePassContractFragmentFragment,
    SignDocumentInput,
    UpdateContractInput
} from '~/resources/graphql/graphql'
import type { FragmentType } from '~/resources/graphql'
import { getFragmentData } from '~/resources/graphql'
import {
    setCartVisible,
    useRedirectOnContractDelete,
    useSetCartData
} from '~/composables/use-cart'
import { useRouter } from 'vue-router'
import DuplicateContractMessage from '~/components/PurchaseContract/DuplicateContractMessage.vue'
import { useRouteLink } from '~/composables/routing'
import PlanPresentation from '~/components/PurchaseContract/PlanPresentation.vue'
import { usePassCreateContractMutation } from '~/components/PurchaseContract/Pass/use-pass-create-contract-mutation'
import { usePassChangeContractMutation } from '~/components/PurchaseContract/Pass/use-pass-change-contract.mutation'
import { usePassUpdateContractMutation } from '~/components/PurchaseContract/Pass/use-pass-update-contract-mutation'
import { usePassSignDocumentMutation } from '~/components/PurchaseContract/Pass/use-pass-sign-document-mutation'
import { useActivateContractMutation } from '~/components/PurchaseContract/use-activate-contract-mutation'
import { useToast } from 'primevue/usetoast'
import CheckoutSection from '~/components/PurchaseContract/CheckoutSection/CheckoutSection.vue'
import { useLayoutMode } from '~/composables/use-layout-mode'
import { useStorefrontPrefix } from '~/composables/use-storefront-prefix'
import { useQueryClient } from '@tanstack/vue-query'
import { getEditingActiveSection } from '~/components/PurchaseContract/purchase-contract-helpers'
import { useOrgShortTitle } from '~/composables/use-org'
import { useHead } from '@unhead/vue'
import { useShowAlert } from '#imports'
import { handleValidationErrors } from '~/components/PurchaseContract/DocumentSection/handle-validation-errors'
import { useAddToCartAnalytics } from '~/composables/analytics/add-to-cart'
import { useI18n } from 'vue-i18n'

const props = withDefaults(
    defineProps<{
        plan: FragmentType<typeof PurchasePassPlanFragment>
        initialContract?: FragmentType<typeof PurchasePassContractFragment>
    }>(),
    {
        initialContract: undefined
    }
)

const planData = computed(() =>
    getFragmentData(PurchasePassPlanFragment, props.plan)
)
const { t } = useI18n({ useScope: 'global' })

const planTitle = computed(() => {
    if (
        planData.value.presentation !== null &&
        planData.value.presentation.title !== ''
    ) {
        return planData.value.presentation.title
    }

    return planData.value.name
})

const shortTitle = useOrgShortTitle()
useHead({
    title: () => shortTitle.value + ' | ' + planTitle.value
})

const { isKiosk } = useLayoutMode()
const { emitAddToCartEvent } = useAddToCartAnalytics()
const isEditing = computed(() => props.initialContract !== undefined)

const allowBookings = computed(
    () => planData.value.checkAllowBookings.allowBookings
)

const contractData = ref<PurchasePassContractFragmentFragment | undefined>(
    props.initialContract !== undefined
        ? getFragmentData(PurchasePassContractFragment, props.initialContract)
        : undefined
)

const activeSection = ref(0)
function setEditingActiveSection() {
    // If we are not editing, return
    if (contractData.value === undefined) {
        return
    }

    activeSection.value = getEditingActiveSection(
        contractData.value.draftExpiresAtUtc,
        contractData.value.readyForPurchase
    )
}
// Run our function on setup
setEditingActiveSection()

const setCartData = useSetCartData()
const { mutate: createContract, isPending: isCreatingContract } =
    usePassCreateContractMutation()
function handleCreateContract(input: CreateContractInput) {
    createContract(input, {
        onSuccess: data => {
            contractData.value = getFragmentData(
                PurchasePassContractFragment,
                data
            )
            activeSection.value = 1
        }
    })
}

const { mutate: changeContract, isPending: isChangingContract } =
    usePassChangeContractMutation()
function handleChangeContract(input: ChangeContractInput) {
    changeContract(input, {
        onSuccess: data => {
            if (data.changeContract.__typename === 'ChangeContractResult') {
                // Update the cart data
                if (data.changeContract.query.viewer !== null) {
                    setCartData(data.changeContract.query.viewer.cart)
                }
                if (data.changeContract.record.__typename === 'PlanContract') {
                    contractData.value = getFragmentData(
                        PurchasePassContractFragment,
                        data.changeContract.record
                    )
                    activeSection.value = 1
                    return
                }
            }

            throw new Error()
        }
    })
}

const { mutateAsync: updateContract, isPending: isUpdatingContract } =
    usePassUpdateContractMutation()
const queryClient = useQueryClient()
function handleContinue(mutationData: UpdateContractInput) {
    updateContract(mutationData, {
        onSuccess: data => {
            if (data.updateContract.__typename === 'UpdateContractResult') {
                // Update the cart data
                if (data.updateContract.query.viewer !== null) {
                    setCartData(data.updateContract.query.viewer.cart)
                }
                if (data.updateContract.record.__typename === 'PlanContract') {
                    contractData.value = getFragmentData(
                        PurchasePassContractFragment,
                        data.updateContract.record
                    )
                    activeSection.value = 2
                    queryClient.invalidateQueries({
                        predicate: query =>
                            query.queryKey.includes('EditDraftPassQuery')
                    })
                    return
                }
            }

            throw new Error()
        }
    })
}

function handleAddToCartParticipantDetails(input: UpdateContractInput) {
    updateContract(input, {
        onSuccess: data => {
            if (data.updateContract.__typename === 'UpdateContractResult') {
                // Update the cart data
                if (data.updateContract.query.viewer !== null) {
                    setCartData(data.updateContract.query.viewer.cart)
                }

                if (data.updateContract.record.__typename === 'PlanContract') {
                    emitAddToCartEvent(data.updateContract.record)
                }

                addToCart()
                return
            }

            throw new Error()
        }
    })
}

const showAlert = useShowAlert()
const toast = useToast()
const { mutate: activateContractMutate, isPending: isActivatingContract } =
    useActivateContractMutation()
async function handleActivateContractNow(input: UpdateContractInput) {
    await updateContract(input, {
        onSuccess: data => {
            if (
                data.updateContract.__typename === 'UpdateContractResult' &&
                data.updateContract.record.__typename === 'PlanContract'
            ) {
                if (data.updateContract.record.__typename === 'PlanContract') {
                    contractData.value = getFragmentData(
                        PurchasePassContractFragment,
                        data.updateContract.record
                    )
                    return
                }
            }

            throw new Error()
        }
    })

    if (contractData.value === undefined) {
        throw new Error()
    }

    activateContractMutate(
        {
            id: contractData.value.id,
            updatedAtUtc: contractData.value.updatedAtUtc,
            documentInput: null
        },
        {
            onSuccess: data => {
                if (
                    data.__typename === 'ActivateContractResult' ||
                    data.__typename === 'ContractIsActive'
                ) {
                    toast.add({
                        severity: 'success',
                        summary: t('purchaseContract.thankYouForPurchase'),
                        detail: planTitle.value,
                        life: 10000,
                        closable: false
                    })
                    router.push(storefrontHomeLink(storefrontPrefix.value))
                } else {
                    showAlert(t('oops'), t('purchaseContract.purchaseFailed'))
                }
            }
        }
    )
}

const invalidInitials = ref(new Set<number>())
const invalidSignatures = ref(new Set<number>())

const { mutate: signDocument, isPending: isSigningDocument } =
    usePassSignDocumentMutation()
function handleSignDocument(mutationData: SignDocumentInput) {
    signDocument(mutationData, {
        onSuccess: data => {
            if (data.signDocument.__typename === 'ValidationError') {
                handleValidationErrors(
                    data.signDocument.fieldErrors,
                    invalidInitials,
                    invalidSignatures
                )
                return
            }

            if (data.signDocument.__typename === 'SignDocumentResult') {
                // Update the cart data
                if (data.signDocument.query.viewer !== null) {
                    setCartData(data.signDocument.query.viewer.cart)
                }

                if (data.signDocument.record.__typename === 'PlanContract') {
                    contractData.value = getFragmentData(
                        PurchasePassContractFragment,
                        data.signDocument.record
                    )
                    return
                }
            }

            throw new Error()
        }
    })
}

const router = useRouter()
const storefrontPrefix = useStorefrontPrefix()
const { storefrontHomeLink } = useRouteLink()
function addToCart() {
    setCartVisible(true)
    router.push(storefrontHomeLink(storefrontPrefix.value))
}

watch(
    () => planData.value.id,
    (newValue, oldValue) => {
        if (newValue !== oldValue) {
            // close all the components
            activeSection.value = 0
            // Remove contract data
            contractData.value = undefined
        }
    }
)

useRedirectOnContractDelete(() => contractData.value?.id)
</script>
